import { useCallback, useEffect, useState } from "react";
import { Download, FileUp, FolderOpen, History, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = ["Handbook", "Policy", "Procedure", "Schedule", "Form", "Map", "Other"];

async function download(url, filename) {
  const response = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}

const UploadForm = ({ clientId, target, onDone, onCancel }) => {
  const [form, setForm] = useState({ title: target?.title || "", category: target?.category || "Handbook", version_date: new Date().toISOString().slice(0, 10), note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event) {
    event.preventDefault();
    if (!file) return setError("Choose a file to upload");
    setBusy(true);
    setError("");
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => body.append(key, value));
    if (target) body.append("document_id", target.id);
    body.append("file", file);
    try {
      const response = await api.post(`/clients/${clientId}/documents`, body, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(response.data, target ? `New version v${response.data.version} of ${response.data.title} saved.` : `${response.data.title} added to the library.`);
    } catch (requestError) {
      setError(formatApiError(requestError));
      setBusy(false);
    }
  }
  return (
    <form className="drawer-form-grid client-doc-upload" data-testid="client-doc-upload-form" onSubmit={submit}>
      <div className="form-section-label drawer-form-full">{target ? `Upload a new version of “${target.title}”` : "Add a document"}</div>
      {error && <p className="form-message error-message drawer-form-full" data-testid="client-doc-upload-error">{error}</p>}
      <label><span className="field-label">Title</span><input data-testid="client-doc-title" disabled={Boolean(target)} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
      <label><span className="field-label">Category</span><select data-testid="client-doc-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
      <label><span className="field-label">Version date</span><input data-testid="client-doc-version-date" required type="date" value={form.version_date} onChange={(e) => setForm({ ...form, version_date: e.target.value })} /></label>
      <label><span className="field-label">File</span><input accept=".pdf,.doc,.docx,.xlsx,.png,.jpg,.jpeg" data-testid="client-doc-file" required type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} /></label>
      <label className="drawer-form-full"><span className="field-label">What changed <em className="field-optional">· optional</em></span><input data-testid="client-doc-note" placeholder="Shown to providers with the new version" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></label>
      <div className="drawer-form-full form-actions">
        <button className="button button-secondary" data-testid="client-doc-cancel" type="button" onClick={onCancel}>Cancel</button>
        <button className="button button-primary" data-testid="client-doc-submit" disabled={busy} type="submit"><FileUp size={15} /> {busy ? "Uploading…" : target ? "Upload new version" : "Add document"}</button>
      </div>
    </form>
  );
};

export const ClientDocumentLibrary = ({ client, onClose, onChanged }) => {
  const [documents, setDocuments] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [history, setHistory] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => api.get(`/clients/${client.id}/documents`).then((response) => setDocuments(response.data)).catch((e) => setError(formatApiError(e))), [client.id]);
  useEffect(() => { load(); }, [load]);

  async function remove(document) {
    if (!window.confirm(`Remove “${document.title}” from the ${client.name} library? Providers placed here will be told it no longer applies.`)) return;
    try {
      await api.delete(`/clients/${client.id}/documents/${document.id}`);
      setMessage(`${document.title} removed.`);
      onChanged?.();
      load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <section className="inline-edit-panel client-doc-library" data-testid="client-doc-library">
      <div className="section-heading">
        <div>
          <h2><FolderOpen size={17} /> {client.name} · document library</h2>
          <p>Handbooks, policies and site documents with version dates. Every new version goes out with “Assignment updated” and must be acknowledged by each provider placed here.</p>
        </div>
        <button aria-label="Close" className="projection-icon-button" data-testid="client-doc-close" type="button" onClick={onClose}><X size={16} /></button>
      </div>
      {error && <p className="form-message error-message" data-testid="client-doc-error">{error}</p>}
      {message && <p className="form-message success-message" data-testid="client-doc-message">{message}</p>}
      {uploading !== null ? (
        <UploadForm clientId={client.id} target={uploading || null} onCancel={() => setUploading(null)} onDone={(doc, text) => { setUploading(null); setMessage(text + (doc.assignments_updated ? ` ${doc.assignments_updated} provider${doc.assignments_updated === 1 ? "" : "s"} sent “Assignment updated”.` : "")); onChanged?.(); load(); }} />
      ) : (
        <button className="button button-primary" data-testid="client-doc-add" type="button" onClick={() => setUploading("")}><FileUp size={15} /> Add document</button>
      )}
      {documents && documents.length === 0 && <p className="pairing-meta" data-testid="client-doc-empty">No documents yet.</p>}
      {documents && documents.length > 0 && (
        <ul className="client-doc-list" data-testid="client-doc-list">
          {documents.map((document) => (
            <li key={document.id} data-testid={`client-doc-${document.id}`}>
              <div>
                <strong>{document.title}</strong>
                <small>{document.category} · v{document.version} dated {document.version_date} · {document.filename} · uploaded {new Date(document.uploaded_at).toLocaleDateString()} by {document.uploaded_by_name}{document.note ? ` · ${document.note}` : ""}</small>
                {history === document.id && (
                  <ol className="client-doc-versions" data-testid={`client-doc-versions-${document.id}`}>
                    {[...document.versions].reverse().map((version) => (
                      <li key={version.version}>v{version.version} · dated {version.version_date} · {version.filename} · {new Date(version.uploaded_at).toLocaleDateString()} by {version.uploaded_by_name}{version.note ? ` · ${version.note}` : ""} <button className="link-button" data-testid={`client-doc-download-${document.id}-v${version.version}`} type="button" onClick={() => download(`/clients/${client.id}/documents/${document.id}/download?version=${version.version}`, version.filename)}>download</button></li>
                    ))}
                  </ol>
                )}
              </div>
              <div className="client-doc-actions">
                <button aria-label="Download current version" className="projection-icon-button" data-testid={`client-doc-download-${document.id}`} title="Download" type="button" onClick={() => download(`/clients/${client.id}/documents/${document.id}/download`, document.filename)}><Download size={14} /></button>
                <button aria-label="Version history" className="projection-icon-button" data-testid={`client-doc-history-${document.id}`} title="Versions" type="button" onClick={() => setHistory(history === document.id ? null : document.id)}><History size={14} /></button>
                <button aria-label="Upload new version" className="projection-icon-button" data-testid={`client-doc-new-version-${document.id}`} title="New version" type="button" onClick={() => setUploading(document)}><FileUp size={14} /></button>
                <button aria-label="Remove document" className="projection-icon-button" data-testid={`client-doc-remove-${document.id}`} title="Remove" type="button" onClick={() => remove(document)}><Trash2 size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
